import { CredentialResponse } from 'google-one-tap'
import { defineStore } from 'pinia'
import { ref } from 'vue'

import { isPWARuntime } from '@/components_new/PWAInstall/utils'
import { useLoggerStore } from '@/store/logger'
import { useUserStore } from '@/store/user-store/user-store'

import { useConfig } from '../composables/config'

export const useGoogleAuth = defineStore('googleAuth', () => {
    const { VUE_APP_GAUTH_REDIRECT, VUE_APP_GAUTH_CLID } = useConfig()
    const loggerStore = useLoggerStore()
    const userStore = useUserStore()
    const onFinishResolver = ref<((result: void) => void) | null>(null)
    const initialized = ref<boolean>(false)
    const promptDisplayed = ref<boolean>(false)

    function onFinishSignIn(onFinish: (() => void) | undefined) {
        return new Promise<void>((resolve) => {
            onFinishResolver.value = () => {
                onFinish?.()
                resolve()
            }
        })
    }

    function setPrompt() {
        // invoke prompt only for web
        if (window.google && !isPWARuntime() && !userStore.isAuthorized) {
            window.google.accounts.id.prompt((notification) => {
                // https://developers.google.com/identity/gsi/web/reference/js-reference#PromptMomentNotification
                if (typeof notification.isDisplayed === 'function') {
                    promptDisplayed.value = notification.isDisplayed()
                } else {
                    promptDisplayed.value = notification.getMomentType() === 'display'
                }
            })
        }
    }

    function cancelPrompt() {
        google.accounts.id.cancel()
    }

    /* should be used only once as google initialize should be invoked only once! -> see more https://developers.google.com/identity/gsi/web/reference/js-reference */
    function initGlobalSignIn() {
        if (!window.google || initialized.value) {
            return
        }
        window.google.accounts.id.initialize({
            client_id: VUE_APP_GAUTH_CLID || '',
            auto_select: false,
            cancel_on_tap_outside: false,
            use_fedcm_for_prompt: true,
            callback: handleAuthication,
        })
        initialized.value = true
    }
    const handleAuthication = async ({ credential }: CredentialResponse): Promise<void> => {
        await fetch(VUE_APP_GAUTH_REDIRECT || '', {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ credential }),
        })

        await userStore.fetchUserInfo()
        loggerStore.logEvent({
            event: 'custom_event',
            eventName: 'login',
            action: userStore.isAuthorized ? 'success' : 'fail',
        })
        if (promptDisplayed.value && userStore.isAuthorized) {
            promptDisplayed.value = false
        }
        if (onFinishResolver.value) {
            onFinishResolver.value()
            onFinishResolver.value = null
        }
    }
    return {
        initGlobalSignIn,
        setPrompt,
        cancelPrompt,
        onFinishSignIn,
        initialized,
        promptDisplayed,
    }
})
